import { useState, useEffect } from "react";
import { getNounsPool } from "../EnglishTool/pool/nounsPool";
import { getVerbsPool } from "../EnglishTool/pool/verbsPool";
import { getAdjectivesPool } from "../EnglishTool/pool/adjectivesPool";
import { getAdverbsPool } from "../EnglishTool/pool/adverbsPool";
import SpeakButton from "../EnglishTool/SpeakButton";
import { awardToken } from "../../awardtoken";
import { db } from "../../firebase";
import { doc, setDoc, serverTimestamp } from "firebase/firestore";
import { useAuth } from "../../components/useAuth";
import './FlashcardsGame.css';

const TOOL_TYPE = "EnglishFlashcards9Memory";
const GRADE_OPTIONS = Array.from({ length: 12 }, (_, i) => i + 1);
const TYPES = ["noun", "verb", "adjective", "adverb"];
const GRID_SIZE = 9;

// ---- TTS: Speak a word out loud ----
function speakWord(word) {
  if (!window.speechSynthesis) return;
  const utter = new window.SpeechSynthesisUtterance(word);
  utter.lang = 'en-US';
  window.speechSynthesis.cancel();
  window.speechSynthesis.speak(utter);
}

function wrapWordArray(arr) {
  return (arr || []).map(w => (typeof w === "string" ? { word: w } : w));
}

function getPool(type, grade) {
  switch(type) {
    case "noun":      return getNounsPool(grade);
    case "verb":      return wrapWordArray(getVerbsPool(grade));
    case "adjective": return wrapWordArray(getAdjectivesPool(grade));
    case "adverb":    return wrapWordArray(getAdverbsPool(grade));
    default:          return [];
  }
}

function getRandomCards(pool, count) {
  const shuffled = pool.slice().sort(() => Math.random() - 0.5);
  return shuffled.slice(0, count).map(card => ({
    ...card,
    previewed: false,   // Was flipped during preview phase
    flipped: false,     // Currently flipped for preview animation
    inputMode: false,   // Ready to accept answer during recall
    recalled: false,    // Answered correctly during recall
    userInput: "",      // Current input for recall
    feedback: "",       // Feedback for recall input
  }));
}

async function logPractice({ user, type, grade, score, mistakes, endTime, startTime }) {
  if (!user?.uid) return;
  await setDoc(
    doc(db, "users", user.uid, "practiceRecords", TOOL_TYPE),
    {
      toolType: TOOL_TYPE,
      topic: `${type} 9-cards memory recall`,
      grade,
      score,
      mistakes,
      total: GRID_SIZE,
      timestamp: serverTimestamp(),
      startedAt: startTime,
      finishedAt: endTime,
      durationSec: Math.round((endTime - startTime) / 1000),
    },
    { merge: true }
  );
}

export default function EnglishFlashcards9_Memory({ onAwardTokens }) {
  const [grade, setGrade] = useState(3);
  const [type, setType] = useState("noun");
  const [pool, setPool] = useState(() => getPool("noun", 3));
  const [cards, setCards] = useState(() => getRandomCards(getPool("noun", 3), GRID_SIZE));
  const [phase, setPhase] = useState("preview"); // 'preview' or 'recall' or 'result'
  const [score, setScore] = useState(0);
  const [mistakes, setMistakes] = useState(0);
  const [sessionStart, setSessionStart] = useState(Date.now());
  const { user } = useAuth();

  // Reset on type/grade change
  useEffect(() => {
    const p = getPool(type, grade);
    setPool(p);
    setCards(getRandomCards(p, GRID_SIZE));
    setPhase("preview");
    setScore(0);
    setMistakes(0);
    setSessionStart(Date.now());
  }, [type, grade]);

  // Check for phase switch after all previewed
  useEffect(() => {
    if (phase === "preview" && cards.every(c => c.previewed)) {
      setTimeout(() => setPhase("recall"), 600); // Brief pause before recall
    }
  }, [cards, phase]);

  // Auto-speak the word when any card is flipped open during preview
useEffect(() => {
  if (phase !== "preview") return;
  const flippedIdx = cards.findIndex(c => c.flipped && !c.previewed);
  if (flippedIdx !== -1) {
    speakWord(cards[flippedIdx].word);
  }
  // Only speak if a card just flipped and not yet previewed.
  // eslint-disable-next-line
}, [cards, phase]);
  // When all recalled, end session
  useEffect(() => {
    if (phase === "recall" && cards.every(c => c.recalled)) {
      setPhase("result");
      const endTime = Date.now();
      logPractice({
        user,
        type,
        grade,
        score: cards.filter(c => c.recalled).length,
        mistakes,
        endTime,
        startTime: sessionStart
      });
      // Only award token if 9/9 correct with **zero mistakes** (perfect score)
      if (onAwardTokens && mistakes === 0 && cards.every(c => c.recalled)) awardToken();
    }
  }, [cards, phase, user, type, grade, sessionStart, mistakes, onAwardTokens]);

  // ---- Preview Phase: Flip, read word, then auto flip back ----
  function handlePreviewFlip(idx) {
    if (phase !== "preview" || cards[idx].previewed || cards[idx].flipped) return;
    setCards(cards =>
      cards.map((card, i) =>
        i === idx ? { ...card, flipped: true } : card
      )
    );
    
    setTimeout(() => {
      setCards(cards =>
        cards.map((card, i) =>
          i === idx
            ? { ...card, flipped: false, previewed: true }
            : card
        )
      );
    }, 1300); // Show for 1.3s
  }

  // ---- Recall Phase: Input mode ----
  function handleRecallInput(idx) {
    if (phase !== "recall" || cards[idx].recalled || cards[idx].inputMode) return;
    setCards(cards =>
      cards.map((card, i) =>
        i === idx ? { ...card, inputMode: true } : card
      )
    );
  }

  function handleRecallSubmit(idx) {
    setCards(cards => cards.map((card, i) => {
      if (i !== idx) return card;
      const correct = card.userInput.trim().toLowerCase() === card.word.toLowerCase();
      if (!correct) setMistakes(m => m + 1);
      return correct
        ? { ...card, inputMode: false, recalled: true, feedback: "Correct!" }
        : { ...card, feedback: "Try again!" };
    }));
    setScore(s => cards[idx].userInput.trim().toLowerCase() === cards[idx].word.toLowerCase() ? s + 1 : s);
  }

  function handleRecallInputChange(idx, val) {
    setCards(cards => cards.map((card, i) =>
      i === idx ? { ...card, userInput: val, feedback: "" } : card
    ));
  }

  function handleRestart() {
    setCards(getRandomCards(pool, GRID_SIZE));
    setPhase("preview");
    setScore(0);
    setMistakes(0);
    setSessionStart(Date.now());
  }

  return (
    <div className="english-practice-card">
      <h2>9-Card Flashcards Game</h2>
      <div>
        <label>Grade: </label>
        <select value={grade} onChange={e => setGrade(Number(e.target.value))}>
          {GRADE_OPTIONS.map(g => <option key={g} value={g}>{g}</option>)}
        </select>
        <label style={{ marginLeft: 16 }}>Type: </label>
        <select value={type} onChange={e => setType(e.target.value)}>
          {TYPES.map(t => <option key={t} value={t}>{t.charAt(0).toUpperCase() + t.slice(1)}</option>)}
        </select>
      </div>

      <div className="nine-grid-wrapper">
        <div className="nine-grid">
          {cards.map((card, idx) => (
            <div
              key={idx}
              className={
                "card" +
                (card.previewed ? " previewed" : "") +
                (card.flipped ? " flipped" : "") +
                (card.recalled ? " recalled" : "")
              }
              onClick={() => {
                if (phase === "preview") handlePreviewFlip(idx);
                else if (phase === "recall") handleRecallInput(idx);
              }}
            >
              {phase === "preview" ? (
                card.flipped ? (
                  <div className="card-word">{card.word}</div>
                ) : (
                  <span className="card-question">?</span>
                )
              ) : phase === "recall" ? (
                card.recalled ? (
                  <div className="card-word correct">
                    {card.word}
                    <span className="correct-badge">✔️</span>
                  </div>
                ) : card.inputMode ? (
                  <form
                    className="card-recall-form"
                    onSubmit={e => {
                      e.preventDefault();
                      handleRecallSubmit(idx);
                    }}
                  >
                    <input
                      className="card-input"
                      value={card.userInput}
                      onChange={e => handleRecallInputChange(idx, e.target.value)}
                      autoFocus
                      autoComplete="off"
                    />
                    <button className="recall-submit-btn" type="submit">Submit</button>
                    {card.feedback && <div className="recall-feedback">{card.feedback}</div>}
                  </form>
                ) : (
                  <span className="card-question">?</span>
                )
              ) : (
                <span className="card-emoji" role="img" aria-label="done">🎉</span>
              )}
            </div>
          ))}
        </div>
      </div>
      <div className="flashcards-stats">
        {phase === "result" ? (
          <>
            <div>
              🎉 <strong>Congratulations!</strong><br />
              Total Correct: {score} / {GRID_SIZE}<br />
              {mistakes === 0 && score === GRID_SIZE && (
                <span style={{ color: "#22bb66", fontWeight: 500 }}>
                  Perfect Score! +1 Token!
                </span>
              )}
              {mistakes > 0 && (
                <span style={{ color: "#bb2222", fontWeight: 500 }}>
                  Mistakes: {mistakes}
                </span>
              )}
            </div>
            <button onClick={handleRestart} className="restart-btn">Restart</button>
          </>
        ) : phase === "recall" ? (
          <div>
            Recall Phase: Type your answer for each card!
            <br />
            Score: {cards.filter(c => c.recalled).length} / {GRID_SIZE}
          </div>
        ) : (
          <div>
            Preview Phase: Click each card to flip and remember!
            <br />
            Cards previewed: {cards.filter(c => c.previewed).length} / {GRID_SIZE}
          </div>
        )}
      </div>
    </div>
  );
}
